import { memo, useEffect, useRef } from 'preact/compat';

import WebApp from '@twa-dev/sdk';
import cn from 'clsx';

import { withErrorBoundary } from '@/app/hocs/withErrorBoundary';
import { useIsTopBarVisible } from '@/app/hooks/use-is-top-bar-visible';

import { useIFrameCommunication } from '@/entities/playdeck/hooks/use-iframe-communication';

import { type Game, GameOrientation } from '@/shared/api/game/types';
import { getAndroidInfo } from '@/shared/helpers/environment/get-android-info';
import { isDesktop } from '@/shared/helpers/is-desktop';
import { useLockOrientation } from '@/shared/hooks/use-orientation/use-lock-orientation';
import { ErrorLogger } from '@/shared/service/logger/ErrorLogger';
import { useAppState, useGameUrl } from '@/shared/stores/app-store/app-store';

import { TopBar } from '@/widget/topbar/ui';

import s from './index.module.scss';

interface Props {
  className?: string;
  game: Game;
}

const GameIframeInner = memo<Props>(({ className, game }) => {
  const gameIframeRef = useRef<HTMLIFrameElement>(null);

  const gameUrl = useGameUrl();

  const isVisibleTopBar = useIsTopBarVisible();
  const { screenOrientation } = useAppState();

  useIFrameCommunication({ gameIframeRef, game });

  const hasTopBar =
    isVisibleTopBar &&
    ((!game.flags.fullscreen && screenOrientation === GameOrientation.PORTRAIT) ||
      isDesktop());

  const handleError = () => {
    ErrorLogger.error('Game Iframe Load Error', 'iframe.onerror', {
      url: game.gameUrl,
    });
  };

  useLockOrientation(game.orientation);

  const isTMAVersionAtLeast8 = WebApp.version && parseFloat(WebApp.version) >= 8.0;

  useEffect(() => {
    if (!isDesktop() && 'requestFullscreen' in WebApp) {
      const androidInfo = getAndroidInfo();

      // Disable fullscreen for Android versions below 11 because of the issue with performance
      if (androidInfo?.majorAndroidVersion && androidInfo.majorAndroidVersion < 11) {
        return;
      }

      if (!WebApp.isFullscreen && isTMAVersionAtLeast8) {
        WebApp.requestFullscreen();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={cn(
        s.container,
        game.orientation && s[game.orientation.toLowerCase()],
        hasTopBar && s['has-top-bar'],
        game.flags.fullscreen && s['fullscreen'],
        className
      )}
    >
      <TopBar />
      <iframe
        ref={gameIframeRef}
        src={gameUrl ?? game.gameUrl}
        className={s.iframe}
        title="Game"
        allowFullScreen
        allow="microphone *; clipboard-read *; clipboard-write *;autoplay *;"
        onError={handleError}
        loading="lazy"
      />
    </div>
  );
});

export const GameIframe = withErrorBoundary(GameIframeInner);
